import React from "react";
import { FormattedNumber, formattedNumber } from "../FormattedNumber/FormattedNumber";
import {
  OrderStatus,
  TradeStatus,
  TimeFrame,
  getOrderTypeName,
  getTimeFrameName,
  getStatusName,
  getTradeStatusName,
  ProductType,
  OrderPriceType,
  Type,
} from "../../utils/Order/Order";
import { Badge } from "reactstrap";
import classnames from "classnames";
import style from "./Order.module.scss";
import { UI } from "..";

/* Order quantity */
export const renderOrderQuantity = (order) => {
  const isMultiAccounts = order.OrderHasMultipleAccounts;

  if (
    !isMultiAccounts &&
    order.AssetClassForm == ProductType.MFUNDS &&
    order.OrderSide == "Sell" &&
    order.CurrentAccount &&
    order.CurrentAccount.IsFullLiquidation
  ) {
    return <span className={style.bold}>Full liq.</span>;
  }

  if (order.AssetClassForm === ProductType.REPO) {
    return (
      <span className={style.bold}>
        <FormattedNumber value={order.CurrentAccount.OffsetQuantity} dataTypeDecimalName="quantity" />
      </span>
    );
  }

  return (
    <span className={style.bold}>
      <FormattedNumber value={order.OrderQuantity} dataTypeDecimalName="quantity" />
    </span>
  );
};

/* Trade quantity */
export const renderTradeQuantity = (trade) => {
  return (
    <span className={style.bold}>
      <FormattedNumber value={trade.TradeQuantity} dataTypeDecimalName="quantity" />
    </span>
  );
};

/* Order type */
export const renderOrderQuantityType = (order) => {
  if (order["AssetClassForm"] == ProductType.MFUNDS) {
    return <span className={style.bold}>{order.OrderQuantityIsCash ? "(Cash)" : "(Shares)"}</span>;
  }

  return null;
};

/* Order price, typePrice */
export const renderOrderPrice = (order) => {
  let price;

  if (
    [
      ProductType.EQUITY,
      ProductType.OPTION,
      ProductType.FOREX,
      ProductType.BOND,
      ProductType.MFUNDS,
      ProductType.MMARKET,
    ].indexOf(order["AssetClassForm"]) >= 0
  ) {
    if (
      order.OrderType == OrderPriceType.MARKET ||
      order.OrderType == OrderPriceType.MARKETONOPEN ||
      order.OrderType == OrderPriceType.MARKETONCLOSE
    ) {
      price = null;
    } else if (order.OrderType == OrderPriceType.STOPLIMIT) {
      price = (
        <span>
          <FormattedNumber
            value={order.OrderPrice}
            decimalScale={order.SubAssetClassDecimalPlaces}
            dataTypeDecimalName="price"
          />
          <FormattedNumber
            value={order.OrderStopPrice}
            decimalScale={order.SubAssetClassDecimalPlaces}
            dataTypeDecimalName="price"
          />
        </span>
      );
    } else if (order.OrderType == OrderPriceType.STOP) {
      price = (
        <span>
          <FormattedNumber
            value={order.OrderStopPrice}
            decimalScale={order.SubAssetClassDecimalPlaces}
            dataTypeDecimalName="price"
          />
        </span>
      );
    } else {
      price = (
        <span>
          <FormattedNumber
            value={order.OrderPrice}
            decimalScale={order.SubAssetClassDecimalPlaces}
            dataTypeDecimalName="price"
          />
        </span>
      );
    }

    return (
      <span className={style.bold}>
        {price} {getOrderTypeName(order.OrderType)}
      </span>
    );
  } else if (ProductType.MFUNDS != order["AssetClassForm"] && order["OrderPrice"] != undefined) {
    return (
      <span className={style.bold}>
        <FormattedNumber
          value={order.OrderPrice}
          decimalScale={order.SubAssetClassDecimalPlaces}
          dataTypeDecimalName="price"
        />
      </span>
    );
  } else {
    return null;
  }
};

/* Trade price, typePrice */
export const renderTradePrice = (trade) => {
  return (
    <span className={style.bold}>
      <FormattedNumber
        value={trade.TradeAvgPrice}
        decimalScale={trade.SubAssetClassDecimalPlaces}
        dataTypeDecimalName="price"
      />
    </span>
  );
};

/* Order timeframe  */
export const renderOrderTimeFrame = (order) => {
  if ([ProductType.EQUITY, ProductType.OPTION, ProductType.FOREX].indexOf(order["AssetClassForm"]) >= 0) {
    const timeFrameName = getTimeFrameName(order.OrderTimeInForce);

    return [
      <Badge color="teal" className={style.badge} key={`timeframe1-${order.OrderId}`}>
        {timeFrameName}
      </Badge>,
      <span key={`timeframe2-${order.OrderId}`}>
        {order.OrderTimeInForce == TimeFrame.GTD ? order.OrderGTD : null}
      </span>,
    ];
  } else {
    return null;
  }
};

/** Order TimeFrame LT */
export const renderOrderTimeFrameLowTouch = (order) => {
  const timeFrameName = getTimeFrameName(order.OrderTimeInForce);

  return [
    <Badge color="teal" className={style.badge} key={`timeframe1-${order.OrderId}`}>
      {timeFrameName}
    </Badge>,
    <span key={`timeframe2-${order.OrderId}`}>
      {order.OrderTimeInForce == TimeFrame.GTD ? order.OrderGTD : null}
    </span>,
  ];
};
/* Order side */
export const renderOrderSide = (order) => {
  let orderSide = order.OrderSide.toUpperCase();
  if (order.AssetClassForm == ProductType.REPO) {
    orderSide = orderSide === "BUY" ? "LEND" : "BORROW";
  }

  return (
    <Badge color={order.OrderSide.toLowerCase()} className={style.badge}>
      {orderSide}
      {order.AssetClassForm == ProductType.OPTION ? `-${order.OrderOpenClose.substring(0, 1)}` : null}
    </Badge>
  );
};

/* Trade side */
export const renderTradeSide = (trade) => {
  return (
    <Badge color={trade.TradeSide.toLowerCase()} className={style.badge}>
      {trade.TradeSide.toUpperCase()}
      {trade.TradeAssetClassForm == ProductType.OPTION ? `-${trade.TradeOpenClose.substring(0, 1)}` : null}
    </Badge>
  );
};

/* Order status */
export const renderOrderStatus = (order) => {
  return (
    <Badge
      className={classnames({ [style.status]: true }, { [style[getStatusClass(order.OrderStatus)]]: true })}
    >
      {getStatusName(order.OrderStatus)}
    </Badge>
  );
};

/* Trade status */
export const renderTradeStatus = (trade) => {
  return (
    <Badge
      className={classnames(
        { [style.status]: true },
        { [style[getTradeStatusClass(trade.TradeStatus)]]: true }
      )}
    >
      {getTradeStatusName(trade.TradeStatus)}
    </Badge>
  );
};

/* Order symbol */
export const renderOrderSymbol = (order) => {
  if (order.AssetClassForm == ProductType.REPO) {
    if (order.OrderSide === "Sell" && order.CurrentAccount.Collateral) {
      const collateral = order.CurrentAccount.Collateral[0];
      // tiene collateral
      return <span className={style.bold}>{collateral.AssetTicker}</span>;
    } else {
      // usa la moneda, OrderSettlementCurrencySymbol
      return <span className={style.bold}>{order.OrderSettlementCurrencySymbol}</span>;
    }
  }

  let settleText = "";
  if (order.AssetClassForm == ProductType.FOREX) {
    return (
      <span className={style.bold}>
        {order.AssetTicker}
        {" - "}
        {order.OrderSettlementCurrencySymbol}
      </span>
    );
  } else {
    if (order.OrderCurrencySymbol != order.OrderSettlementCurrencySymbol) {
      settleText = ` (stl ${order.OrderSettlementCurrencySymbol})`;
    }
  }
  return (
    <UI.Tooltip text={order.AssetISIN ? order.AssetISIN : "Missing ISIN"}>
      <span className={style.bold}>
        {order.AssetTicker}
        {settleText}
      </span>
    </UI.Tooltip>
  );
};

/* Trade symbol */
export const renderTradeSymbol = (trade) => {
  if (trade.TradeAssetClassForm == ProductType.FOREX) {
    return (
      <span className={style.bold}>
        {trade.TradeAssetTicker}
        {" - "}
        {trade.TradeSettleCurrencyISO}
      </span>
    );
  }
  return (
    <UI.Tooltip text={trade.TradeAssetISIN ? trade.TradeAssetISIN : "Missing ISIN"}>
      <span className={style.bold}>{trade.TradeAssetTicker}</span>
    </UI.Tooltip>
  );
};

/* Order broker */
export const renderOrderBroker = (brokerName) => {
  const abrev = brokerName.substr(0, 3).toUpperCase();
  return <span className={style.broker}>{abrev}</span>;
};

/* Order account */
export const renderOrderAccount = (order) => {
  let number;
  if (order.Accounts.length == 0) {
    if (order.CurrentAccount) {
      number = order.CurrentAccount.AccountNumber;
    } else {
      number = "No accounts";
    }
  } else if (order.Accounts.length == 1) {
    number = order.Accounts[0].AccountNumber;
  } else {
    number = "Multiple accts";
  }
  return <span className={style.bold}>{number}</span>;
};

/* Order account */
export const renderOrderAccountName = (order) => {
  let name;
  if (order.Accounts.length == 0) {
    if (order.CurrentAccount) {
      name = order.CurrentAccount.AccountName;
    } else {
      name = "No accounts";
    }
  } else if (order.Accounts.length == 1) {
    name = order.Accounts[0].AccountName;
  } else {
    name = "Multiple accts";
  }
  return <span className={style.bold}>{name}</span>;
};

/* Order instruction */
export const renderOrderInstruction = (order) => {
  let instruction = "";
  if (order.Accounts.length == 0) {
    if (order.CurrentAccount) {
      instruction = order.CurrentAccount.Instruction;
    } else {
      instruction = "No accounts";
    }
  } else if (order.Accounts.length == 1) {
    instruction = order.Accounts[0].Instruction;
  } else {
    let hasDifferentInstructions = false;
    order.Accounts.forEach((account) => {
      if (instruction == "") {
        instruction = account.Instruction;
      } else if (account.Instruction !== instruction) {
        hasDifferentInstructions = true;
      }
    });
    if (hasDifferentInstructions) {
      instruction = "Multiple inst.";
    }
  }
  return <span className={style.bold}>{instruction}</span>;
};

/* Order currencies */
export const renderOrderCurrencies = (order) => {
  return (
    <span className={style.bold}>
      (
      {order.OrderCurrencySymbol == order.OrderSettlementCurrencySymbol
        ? order.OrderCurrencySymbol
        : `${order.OrderCurrencySymbol}/${order.OrderSettlementCurrencySymbol}`}
      )
    </span>
  );
};

export const renderExecutionsAvg = (order) => {
  const { pending } = order.stats;

  let _pending = parseFloat(pending);

  if (pending > 999) {
    _pending = Math.round((pending / 1000) * 100) / 100 + "K";
  } else {
    _pending = parseFloat(_pending.toFixed(1));
  }
  const OrderAverage = ({ avg }) => {
    let res = avg + "";
    if (res === "-") return res;

    if (res.includes("K"))
      return formattedNumber(res.split(0, -1), "quantity", null, true) + res[res.length - 1];
    else return formattedNumber(res, "quantity", null, true);
  };

  return (
    <span className={style.avg}>
      {formattedNumber(_pending, "quantity", null, true)}
      {"/"}
      <OrderAverage avg={order.stats.avg} />
    </span>
  );
};

const getStatusClass = (status) => {
  switch (status) {
    case OrderStatus.AMENDING:
      return "color2";
    case OrderStatus.CANCEL_PENDING:
      return "color2";
    case OrderStatus.ROUTED:
      return "color1";
    case OrderStatus.ACKED:
      return "color1";
    case OrderStatus.PART:
      return "color3";
    case OrderStatus.FILLED:
      return "color4";
    case OrderStatus.REJECTED:
      return "color5";
    case OrderStatus.ANNULLED:
    case OrderStatus.CANCELED:
      return "color5";
    case OrderStatus.PART_FILLED:
      return "color4";
    case OrderStatus.PARKED:
      return "color6";
    case OrderStatus.REALLOCATING:
      return "color2";
    default:
      return "";
  }
};

const getTradeStatusClass = (status) => {
  switch (status) {
    case TradeStatus.NEW:
      return "color1";
    case TradeStatus.CONFIRMATION_SENT:
      return "color1";
    case TradeStatus.MATCHED:
      return "color4";
    case TradeStatus.UNMATCHED:
      return "color5";
    case TradeStatus.BOOKED:
      return "color4";
    case TradeStatus.CANCELED:
      return "color5";
    default:
      return "";
  }
};
